import type { ActionContext } from 'js/lib/ActionContext';

import type { CreateProctorConfigOverrideProps } from 'bundles/author-assess-common/data/proctoring/useCreateProctorConfigOverride';
import type { GetProctorConfigOverrideProps } from 'bundles/author-assess-common/data/proctoring/useGetProctorConfigOverride';
import {
  cloneProctorConfigDraft,
  createProctorConfigDraft,
  getPeerReviewProctorConfigDraft,
  getProctorConfigDraft,
  getSpecificProctorConfigDraft,
  publishProctorConfigDraft,
  revertProctorConfigDraft,
  updateProctorConfigDraft,
} from 'bundles/author-assess-common/lib/ProctorConfigApiUtils';

type ProctorConfigChange = {
  maxAttempts?: number;
  timeLimit?: number;
  maxSubmissions?: number;
};

export function localInitProctorConfig(actionContext: ActionContext, proctorConfigDraft: ProctorConfigChange) {
  actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
    proctorConfigDraft,
  });
}

// note: any key set to "null" will remove that limit and allow unlimited attempts, time, submission, etc.
function localUpdateProctorConfig(actionContext: ActionContext, params: ProctorConfigChange) {
  if (Object.prototype.hasOwnProperty.call(params, 'maxAttempts')) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT_MAX_ATTEMPTS', {
      maxAttempts: params.maxAttempts,
    });
  }
  if (Object.prototype.hasOwnProperty.call(params, 'timeLimit')) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT_TIME_LIMIT', {
      timeLimit: params.timeLimit,
    });
  }
  if (Object.prototype.hasOwnProperty.call(params, 'maxSubmissions')) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT_MAX_SUBMISSIONS', {
      maxSubmissions: params.maxSubmissions,
    });
  }
}

export function loadProctorConfig(
  actionContext: ActionContext,
  { draftId, branchId, itemId }: { draftId: string; branchId: string; itemId: string }
) {
  return getProctorConfigDraft(draftId, branchId, itemId).then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
      hasChanges: false,
    });
  });
}

export function resetProctorConfigStore(actionContext: ActionContext) {
  actionContext.dispatch('RESET_PROCTOR_CONFIG_STORE');
}

export function loadPeerReviewProctorConfig(
  actionContext: ActionContext,
  {
    branchId,
    itemId,
    atomId,
    proctorConfigurationId,
  }: {
    branchId: string;
    itemId: string;
    atomId: string;
    proctorConfigurationId: string;
  }
) {
  getPeerReviewProctorConfigDraft(branchId, itemId, atomId).then((peerReviewProctorConfigDraft) => {
    if (peerReviewProctorConfigDraft) {
      actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
        proctorConfigDraft: {
          id: proctorConfigurationId,
          proctorConfigurationId,
          draft: peerReviewProctorConfigDraft,
        },
        hasChanges: false,
      });
    } else {
      /* this resets the store if there is no
       proctor config draft to avoid stale data */
      resetProctorConfigStore(actionContext);
    }
  });
}

type LoadSpecificProctorConfigProps = {
  standardProctorConfigurationId: string;
  userId: string;
  branchId: string;
  itemId: string;
  publishedProctorConfiguration?: {
    maxAttempts?: number;
    timeLimit?: number;
    maxSubmissions?: number;
  };
};

export function loadSpecificProctorConfig(
  actionContext: ActionContext,
  {
    standardProctorConfigurationId,
    userId,
    branchId,
    itemId,
    publishedProctorConfiguration,
  }: LoadSpecificProctorConfigProps
) {
  return getSpecificProctorConfigDraft(standardProctorConfigurationId, userId, branchId, itemId).then(
    (proctorConfigDraft) => {
      const { attemptConfigurations, maxAttempts } = proctorConfigDraft?.draft || {};
      const { timeLimit, maxSubmissions } = attemptConfigurations?.[0] || {};
      const hasChanges =
        publishedProctorConfiguration &&
        (publishedProctorConfiguration?.maxAttempts !== maxAttempts ||
          publishedProctorConfiguration?.timeLimit !== timeLimit?.duration ||
          publishedProctorConfiguration?.maxSubmissions !== maxSubmissions);

      actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', { proctorConfigDraft, hasChanges });
    }
  );
}

export function loadSpecificProctorConfigForPeerReview(
  actionContext: ActionContext,
  {
    branchId,
    itemId,
    atomId,
    proctorConfigurationId,
    context: { proctorConfigOverride, getProctorConfigOverride },
  }: {
    branchId: string;
    itemId: string;
    atomId: string;
    proctorConfigurationId: string;
    context: {
      proctorConfigOverride: GetProctorConfigOverrideProps['proctorConfigOverride'];
      getProctorConfigOverride: GetProctorConfigOverrideProps['getProctorConfigOverride'];
    };
  }
) {
  if (proctorConfigOverride) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft: proctorConfigOverride,
      hasChanges: false,
    });
  } else if (getProctorConfigOverride) {
    /* if no proctor config override exists for this user, load the assignment proctor configuration */
    getProctorConfigOverride()
      ?.then((proctorConfigDraft) => {
        if (proctorConfigDraft) {
          actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
            proctorConfigDraft,
            hasChanges: false,
          });
        } else {
          loadPeerReviewProctorConfig(actionContext, { branchId, itemId, atomId, proctorConfigurationId });
        }
      })
      .catch(() => {
        loadPeerReviewProctorConfig(actionContext, { branchId, itemId, atomId, proctorConfigurationId });
      });
  } else {
    loadPeerReviewProctorConfig(actionContext, { branchId, itemId, atomId, proctorConfigurationId });
  }
}

export function saveProctorConfig(actionContext: ActionContext) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();
  const draft = proctorConfigStore.getDraft();
  const metadata = proctorConfigStore.getDraftMetadata();
  if (id) {
    return updateProctorConfigDraft(id, draft, metadata).then((proctorConfigDraft: $TSFixMe) => {
      actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
        proctorConfigDraft,
      });
    });
  } else {
    return Promise.reject(new Error('Unable to save proctor config: Missing proctor config draft id'));
  }
}

export function createProctorConfig(
  actionContext: ActionContext,
  { branchId, itemId, initialConfig }: { branchId: string; itemId: string; initialConfig: ProctorConfigChange }
) {
  return createProctorConfigDraft(branchId, itemId).then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('INITIALIZE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
    });
    if (initialConfig) {
      localUpdateProctorConfig(actionContext, initialConfig);
      actionContext.executeAction(saveProctorConfig);
    }
    return proctorConfigDraft;
  });
}

export function cloneProctorConfig(
  actionContext: ActionContext,
  { publishedId, branchId, itemId }: { publishedId: string; branchId: string; itemId: string }
) {
  return cloneProctorConfigDraft(publishedId, branchId, itemId).then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
    });
  });
}

export function revertProctorConfig(actionContext: ActionContext) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();
  const metadata = proctorConfigStore.getDraftMetadata();
  return revertProctorConfigDraft(id, metadata).then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
      resetChanges: true,
    });
  });
}

export function clearProctorConfig(actionContext: ActionContext) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();
  actionContext.dispatch('CLEAR_PROCTOR_CONFIG_DRAFT');
  if (id) {
    actionContext.executeAction(saveProctorConfig);
  }
}

export function publishProctorConfig(actionContext: ActionContext) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();
  const metadata = proctorConfigStore.getDraftMetadata();
  return publishProctorConfigDraft(id, metadata).then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
      resetChanges: true,
    });
  });
}

// note: any key set to "null" will remove that limit and allow unlimited attempts, time, submission, etc.
export function updateProctorConfig(actionContext: ActionContext, params: ProctorConfigChange) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();
  localUpdateProctorConfig(actionContext, params);
  if (id) {
    actionContext.executeAction(saveProctorConfig);
  }
}

export function loadLatestProctorConfigOverride(
  actionContext: ActionContext,
  {
    context: { proctorConfigOverride, getProctorConfigOverride },
  }: {
    context: {
      proctorConfigOverride: GetProctorConfigOverrideProps['proctorConfigOverride'];
      getProctorConfigOverride: GetProctorConfigOverrideProps['getProctorConfigOverride'];
    };
  }
) {
  if (proctorConfigOverride) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft: proctorConfigOverride,
      hasChanges: false,
    });
  } else {
    getProctorConfigOverride()?.then((proctorConfigDraft) => {
      if (proctorConfigDraft) {
        actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
          proctorConfigDraft,
          hasChanges: false,
        });
      }
    });
  }
}

// note: any key set to "null" will remove that limit and allow unlimited attempts, time, submission, etc.
export function updateProctorConfigOverride(actionContext: ActionContext, params: ProctorConfigChange) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const id = proctorConfigStore.getId();

  localUpdateProctorConfig(actionContext, params);
  if (id) {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft: {
        id,
        draft: proctorConfigStore.getDraft(),
        proctorConfigurationId: proctorConfigStore.getDraftId(),
      },
    });
  }
}

export function publishProctorConfigOverride(
  actionContext: ActionContext,
  {
    context: { createProctorConfigOverride },
  }: {
    context: {
      createProctorConfigOverride: CreateProctorConfigOverrideProps['createProctorConfigOverride'];
    };
  }
) {
  const proctorConfigStore = actionContext.getStore('ProctorConfigStore');
  const draft = proctorConfigStore.getDraft();

  return createProctorConfigOverride?.({
    maxAttempts: draft.maxAttempts,
    maxSubmissionsPerAttempt: draft.attemptConfigurations?.[0]?.maxSubmissions,
    attemptDuration: draft.attemptConfigurations?.[0]?.timeLimit?.duration || null,
  })?.then((proctorConfigDraft: $TSFixMe) => {
    actionContext.dispatch('RECEIVE_PROCTOR_CONFIG_DRAFT', {
      proctorConfigDraft,
      resetChanges: true,
    });
  });
}
